import { type JSX, useState } from "react";

import { Button, type ButtonVariantType } from "common/components/ui/Button";
import { Dialog, DialogContent } from "common/components/ui/Dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "common/components/ui/Popover";
import { typedObjectEntries } from "common/helpers/utils";

import type { Icon } from "@phosphor-icons/react";

type ActionsType<Item extends object> = Record<
  string,
  {
    icon: Icon;
    getLabel: (eligibleItemsCount: number) => string;
    getEligibleItems: (selectedItems: Item[]) => Item[];
    isDanger?: true;
    isPopup?: true;
    isSecondary?: true;
  }
>;

interface BulkManageDesktopProps<
  Item extends object,
  Actions extends ActionsType<Item>,
> {
  actions: Actions;
  selection: Item[];
  renderActionComponent: ({
    action,
    onClose,
  }: {
    action: keyof Actions;
    onClose: () => void;
  }) => JSX.Element;
}

export default function BulkManageDesktop<
  Item extends object,
  Actions extends ActionsType<Item>,
>({
  actions,
  selection,
  renderActionComponent,
}: BulkManageDesktopProps<Item, Actions>) {
  const [action, setAction] = useState<keyof Actions>();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {action && !actions[action].isPopup && (
        <Dialog
          open={isOpen}
          onOpenChange={setIsOpen}
          onAfterClose={() => setAction(undefined)}
        >
          <DialogContent>
            {renderActionComponent({
              action,
              onClose: () => setIsOpen(false),
            })}
          </DialogContent>
        </Dialog>
      )}

      {typedObjectEntries(actions)
        .filter(
          ([, { getEligibleItems }]) => getEligibleItems(selection).length,
        )
        .map(
          ([
            actionKey,
            {
              icon: ActionIcon,
              getLabel,
              getEligibleItems,
              isDanger,
              isPopup,
              isSecondary,
            },
          ]) => {
            const eligibleCount = getEligibleItems(selection).length;
            let buttonVariant: ButtonVariantType = "outline-black";
            if (isDanger) {
              buttonVariant = "outline-danger";
            } else if (isSecondary) {
              buttonVariant = "secondary-purple";
            }

            const button = (
              <Button
                key={actionKey.toString()}
                size="sm"
                variant={buttonVariant}
                leftIcon={<ActionIcon weight="fill" />}
                type="button"
                onClick={() => {
                  setAction(actionKey);
                  setIsOpen(true);
                }}
              >
                {getLabel(eligibleCount)}
              </Button>
            );

            if (isPopup) {
              return (
                <Popover
                  key={actionKey.toString()}
                  open={isOpen && action === actionKey}
                  onOpenChange={setIsOpen}
                >
                  <PopoverTrigger asChild>{button}</PopoverTrigger>
                  <PopoverContent>
                    {renderActionComponent({
                      action: actionKey,
                      onClose: () => setIsOpen(false),
                    })}
                  </PopoverContent>
                </Popover>
              );
            }

            return button;
          },
        )}
    </>
  );
}
